import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { SelectOption } from '../../../../components/form/select-field/select-field.config';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { CompanyDocumentRevisionAttachmentsComponent } from '../../components/company-document-revision-attachments/company-document-revision-attachments.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { CommonService } from '../../../../services/common.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import {
  QUALITY_MANAGER_PROFILE_CODE,
  normalizeProfileCode
} from '../../../../layouts/main-layout/components/sidebar/config/access-control.config';
import { PlanningUser, planningUserFullName } from '../../../../models/entities/planning';
import {
  COMPANY_DOCUMENT_REVISION_STATUS_BADGES,
  COMPANY_DOCUMENT_REVISION_STATUS_LABELS,
  CompanyDocument,
  CompanyDocumentRevision,
  CompanyDocumentRevisionPayload,
  CompanyDocumentRevisionStatus,
  companyDocumentRevisionInProgressId
} from '../../../../models/entities/company-document';

@Component({
  selector: 'app-company-documents-revision-edit-page',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent,
    LoaderComponent,
    CompanyDocumentRevisionAttachmentsComponent
  ],
  templateUrl: './company-documents-revision-edit-page.component.html',
  styleUrl: './company-documents-revision-edit-page.component.scss',
})
export class CompanyDocumentsRevisionEditPageComponent implements OnInit, OnDestroy {

  @ViewChild(CompanyDocumentRevisionAttachmentsComponent) attachmentsPanel?: CompanyDocumentRevisionAttachmentsComponent;

  companyDocumentId?: number;
  companyDocumentRevisionId?: number;
  companyDocument?: CompanyDocument;
  revision?: CompanyDocumentRevision;
  isEditMode: boolean = false;

  form: FormGroup;
  userOptions: SelectOption[] = [];
  pendingAttachmentFiles: File[] = [];

  canModify: boolean = false;
  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };

  private currentUserId: number | null = null;
  private hasDeleteAccess: boolean = false;
  private isQualityManager: boolean = false;

  private confirmationRequested: boolean = false;

  private readonly LOADER_CONTEXT = 'company-documents-revision-edit-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private qualityManagementService: QualityManagementService,
    private commonService: CommonService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.form = this.formBuilder.group({
      documentDepartment: [{ value: '', disabled: true }],
      documentName: [{ value: '', disabled: true }],
      code: [{ value: '', disabled: true }],
      date: ['', [Validators.required]],
      description: ['', [Validators.required]],
      reviewedUserId: [null, [Validators.required]],
      approvedUserId: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserId = user?.userId != null ? Number(user.userId) : null;
      });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
          'modify'
        );
        this.hasDeleteAccess = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
          'delete'
        );
        this.isQualityManager = normalizeProfileCode(profileCode) === QUALITY_MANAGER_PROFILE_CODE;
        this.applyFieldPermissions();
      });

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    this.loadUsers();

  
    const revisionParamId = this.route.snapshot.paramMap.get('companyDocumentRevisionId');
    if (revisionParamId) {
      this.companyDocumentRevisionId = Number(revisionParamId);
      this.isEditMode = true;
      this.loadRevision(this.companyDocumentRevisionId);
      return;
    }

    const documentParamId = this.route.snapshot.paramMap.get('companyDocumentId');
    if (documentParamId) {
      this.companyDocumentId = Number(documentParamId);
      this.loadCompanyDocument(this.companyDocumentId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }




  get documentDepartment(): string {
    return this.companyDocument?.department ?? '';
  }

  get documentName(): string {
    return this.companyDocument?.name ?? '';
  }

  get code(): string {
    return this.revision?.code ?? '';
  }



  get status(): CompanyDocumentRevisionStatus | null {
    return this.revision?.status ?? null;
  }

  get statusLabel(): string {
    return this.status ? COMPANY_DOCUMENT_REVISION_STATUS_LABELS[this.status] : '';
  }

  get statusBadgeClass(): string {
    return this.status ? COMPANY_DOCUMENT_REVISION_STATUS_BADGES[this.status] : 'bg-secondary';
  }

  get reviewedSigned(): boolean {
    return !!this.revision?.reviewedSigned;
  }

  get approvedSigned(): boolean {
    return !!this.revision?.approvedSigned;
  }

  get reviewedDate(): string {
    return this.formatSignatureDate(this.revision?.reviewedDate);
  }

  get approvedDate(): string {
    return this.formatSignatureDate(this.revision?.approvedDate);
  }


  private toDateInputValue(value?: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : this.commonService.formatDateOnly(date);
  }

  private formatSignatureDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : this.commonService.formatDate(date);
  }

  get attachments() {
    return this.revision?.attachments ?? [];
  }

  get hasAttachment(): boolean {
    return this.attachments.length > 0;
  }



  get canSave(): boolean {
    return this.canModify;
  }

  get attachmentsReadonly(): boolean {
    return !this.canModify
      || this.confirmationRequested
      || this.reviewedSigned
      || this.approvedSigned;
  }

  get canRemoveAttachment(): boolean {
    return this.isEditMode
      && this.status === 'IN_PROGRESS'
      && this.hasAttachment
      && !this.attachmentsReadonly;
  }

  get canRequestConfirmation(): boolean {
    return this.canModify && this.isEditMode && this.hasAttachment;
  }

  get isRequestConfirmationDisabled(): boolean {
    return this.reviewedSigned && this.approvedSigned;
  }

  get canSignAsReviewer(): boolean {
    return this.isEditMode
      && !this.reviewedSigned
      && this.currentUserId != null
      && Number(this.revision?.reviewedUser?.userId) === this.currentUserId;
  }

  get canSignAsApprover(): boolean {
    return this.isEditMode
      && !this.approvedSigned
      && this.currentUserId != null
      && Number(this.revision?.approvedUser?.userId) === this.currentUserId;
  }

  get canMoveInUse(): boolean {
    return this.isQualityManager
      && this.isEditMode
      && this.reviewedSigned
      && this.approvedSigned
      && this.status === 'IN_PROGRESS';
  }

  get canDelete(): boolean {
    if (!this.isEditMode) {
      return false;
    }

    const isCreator = this.currentUserId != null
      && Number(this.companyDocument?.creatorUser?.userId) === this.currentUserId;

    return this.hasDeleteAccess || isCreator;
  }



  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  hasError(name: string): boolean {
    const control = this.form.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  errorMessage(name: string): string {
    const control = this.form.get(name);

    if (!control?.errors) {
      return '';
    }
    if (control.errors['backendError']) {
      return control.errors['backendError'];
    }
    if (control.errors['required']) {
      return 'Required field';
    }
    return '';
  }

  onPendingFilesChange(files: File[]): void {
    this.pendingAttachmentFiles = files;
  }

  onAttachmentsChanged(): void {
    if (this.companyDocumentRevisionId) {
      this.loadRevision(this.companyDocumentRevisionId);
    }
  }




  submitForm(): void {
    if (!this.canSave || !this.companyDocumentId) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.error('Please fill in all the required fields.');
      return;
    }

    const payload = this.buildPayload(this.companyDocumentId);

    const request$ = this.companyDocumentRevisionId
      ? this.qualityManagementService.updateCompanyDocumentRevision(this.companyDocumentRevisionId, payload)
      : this.qualityManagementService.createCompanyDocumentRevision(payload)
          .pipe(switchMap((response: any) => this.uploadPendingFiles(response)));

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    request$.subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        const revision: CompanyDocumentRevision | null =
          this.qualityManagementService.readResponseKey(response, 'companyDocumentRevision') ?? null;

        if (!revision) {
          this.applyBackendErrors(response);
          return;
        }

        const isFirstSave = !this.isEditMode;
        this.applyRevision(revision);
        this.alertNotificationService.success('Revision saved.');

        if (isFirstSave && revision.companyDocumentRevisionId) {
          this.attachmentsPanel?.clearPendingFiles();
          this.pendingAttachmentFiles = [];
          this.routeService.companyDocumentsRevisionDetail(Number(revision.companyDocumentRevisionId));
        }
      },
      error: (error: any) => {
        console.error('Error saving the company document revision:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.applyBackendErrors(error?.error, error);
      }
    });
  }

  requestConfirmation(): void {
    if (!this.canRequestConfirmation || this.isRequestConfirmationDisabled || !this.companyDocumentRevisionId) {
      return;
    }

    this.runAction(
      this.qualityManagementService.requestCompanyDocumentRevisionConfirmation(this.companyDocumentRevisionId),
      'Signature request sent to the reviewer and the approver.',
      'Failed to request the confirmation.',
      () => this.confirmationRequested = true
    );
  }

  signAsReviewer(): void {
    if (!this.canSignAsReviewer || !this.companyDocumentRevisionId) {
      return;
    }

    this.runAction(
      this.qualityManagementService.signCompanyDocumentRevisionReviewed(this.companyDocumentRevisionId),
      'Revision signed as reviewer.',
      'Failed to sign the revision.'
    );
  }

  signAsApprover(): void {
    if (!this.canSignAsApprover || !this.companyDocumentRevisionId) {
      return;
    }

    this.runAction(
      this.qualityManagementService.signCompanyDocumentRevisionApproved(this.companyDocumentRevisionId),
      'Revision signed as approver.',
      'Failed to sign the revision.'
    );
  }

  moveInUse(): void {
    if (!this.canMoveInUse || !this.companyDocumentRevisionId) {
      return;
    }

    this.runAction(
      this.qualityManagementService.moveCompanyDocumentRevisionInUse(this.companyDocumentRevisionId),
      'Revision moved in use.',
      'Failed to move the revision in use.'
    );
  }

  deleteRevision(): void {
    if (!this.canDelete || !this.companyDocumentRevisionId) {
      return;
    }

    this.confirmationDialogService.open(
      `Are you sure to delete revision ${this.code}?`,
      () => this.confirmDeleteRevision()
    );
  }

  goBackToDocument(): void {
    if (!this.companyDocumentId) {
      return;
    }
    this.routeService.companyDocumentsDetail(this.companyDocumentId);
  }



  private confirmDeleteRevision(): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.deleteCompanyDocumentRevision(this.companyDocumentRevisionId!).subscribe({
      next: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.success('Revision deleted.');
        this.goBackToDocument();
      },
      error: (error: any) => {
        console.error('Error deleting the company document revision:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to delete the revision.')
        );
      }
    });
  }


  private runAction(
    request$: Observable<any>,
    successMessage: string,
    fallbackErrorMessage: string,
    onSuccess?: () => void
  ): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    request$.subscribe({
      next: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        onSuccess?.();
        this.alertNotificationService.success(successMessage);

        if (this.companyDocumentRevisionId) {
          this.loadRevision(this.companyDocumentRevisionId);
        }
      },
      error: (error: any) => {
        console.error('Error running the company document revision action:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, fallbackErrorMessage)
        );
      }
    });
  }


  private uploadPendingFiles(createResponse: any): Observable<any> {
    const revisionId = this.qualityManagementService
      .readResponseKey(createResponse, 'companyDocumentRevision')?.companyDocumentRevisionId;

    if (!revisionId || !this.pendingAttachmentFiles.length) {
      return of(createResponse);
    }

    const uploads$ = this.pendingAttachmentFiles.map(file =>
      this.qualityManagementService.uploadCompanyDocumentRevisionAttachment(Number(revisionId), file)
    );

    return forkJoin(uploads$).pipe(switchMap(() => of(createResponse)));
  }

  private buildPayload(companyDocumentId: number): CompanyDocumentRevisionPayload {
    const value = this.form.getRawValue();

    return {
      companyDocumentId,
      date: value.date,
      description: value.description,
      reviewedUserId: Number(value.reviewedUserId),
      approvedUserId: Number(value.approvedUserId)
    };
  }

  private loadUsers(): void {
    const params = new HttpParams().set('size', '9999');

    this.qualityManagementService.listPlanningAssignableUsers({ params }).subscribe({
      next: (response: any) => {
        const users: PlanningUser[] =
          this.qualityManagementService.readResponseKey(response, 'users', 'assignableUsers') || [];
        this.userOptions = [
          { key: '', value: '-- select' },
          ...users.map(user => ({ key: user.userId, value: planningUserFullName(user) }))
        ];
      },
      error: (error: any) => {
        console.error('Error loading the users list:', error);
        this.alertNotificationService.error('Failed to load the users list.');
      }
    });
  }

  private loadCompanyDocument(companyDocumentId: number): void {
    this.qualityManagementService.detailCompanyDocument(companyDocumentId).subscribe({
      next: (response: any) => {
        this.companyDocument =
          this.qualityManagementService.readResponseKey(response, 'companyDocument') ?? undefined;

        const revisionInProgressId = companyDocumentRevisionInProgressId(this.companyDocument);
        if (!this.isEditMode && revisionInProgressId) {
          this.alertNotificationService.warning('A revision is already in progress: opening it.');
          this.routeService.companyDocumentsRevisionDetail(revisionInProgressId);
          return;
        }

        this.applyDocumentContext();
      },
      error: (error: any) => {
        console.error('Error loading the company document:', error);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to load the company document.')
        );
      }
    });
  }

  private loadRevision(companyDocumentRevisionId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailCompanyDocumentRevision(companyDocumentRevisionId).subscribe({
      next: (response: any) => {
        const revision: CompanyDocumentRevision | null =
          this.qualityManagementService.readResponseKey(response, 'companyDocumentRevision') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!revision) {
          this.alertNotificationService.error('Company document revision not found.');
          return;
        }

        this.applyRevision(revision);
      },
      error: (error: any) => {
        console.error('Error loading the company document revision:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to load the revision.')
        );
      }
    });
  }

  private applyRevision(revision: CompanyDocumentRevision): void {
    this.revision = revision;
    this.companyDocumentRevisionId = revision.companyDocumentRevisionId ?? this.companyDocumentRevisionId;
    this.isEditMode = !!this.companyDocumentRevisionId;
    this.companyDocumentId = revision.companyDocumentId
      ?? revision.companyDocument?.companyDocumentId
      ?? this.companyDocumentId;

    if (revision.companyDocument) {
      this.companyDocument = revision.companyDocument;
      this.applyDocumentContext();
    } else if (!this.companyDocument && this.companyDocumentId) {
      this.loadCompanyDocument(this.companyDocumentId);
    }

    this.form.patchValue({
      code: revision.code ?? '',
      date: this.toDateInputValue(revision.date),
      description: revision.description ?? '',
      reviewedUserId: revision.reviewedUser?.userId ?? '',
      approvedUserId: revision.approvedUser?.userId ?? ''
    }, { emitEvent: false });

    this.applyFieldPermissions();
  }

  private applyDocumentContext(): void {
    this.form.patchValue({
      documentDepartment: this.companyDocument?.department ?? '',
      documentName: this.companyDocument?.name ?? ''
    }, { emitEvent: false });
  }

  private applyFieldPermissions(): void {
    if (!this.canModify) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });

    this.form.get('code')?.disable({ emitEvent: false });
    this.form.get('documentDepartment')?.disable({ emitEvent: false });
    this.form.get('documentName')?.disable({ emitEvent: false });

    if (this.reviewedSigned) {
      this.form.get('reviewedUserId')?.disable({ emitEvent: false });
    }
    if (this.approvedSigned) {
      this.form.get('approvedUserId')?.disable({ emitEvent: false });
    }
  }

  private applyBackendErrors(response: any, error?: any): void {
    const validationErrors = response?.validationErrors ?? response?.errors ?? null;

    if (validationErrors && typeof validationErrors === 'object') {
      Object.keys(validationErrors).forEach(field => {
        const control = this.form.get(field);
        if (control) {
          control.markAsTouched();
          control.setErrors({
            ...(control.errors ?? {}),
            backendError: validationErrors[field],
            back: validationErrors[field]
          });
        }
      });

      this.alertNotificationService.error(Object.values(validationErrors).join(' '));
      return;
    }

    this.alertNotificationService.error(
      this.qualityManagementService.companyDocumentErrorMessage(
        error ?? { error: response },
        'Failed to save the revision.'
      )
    );
  }
}
